import { RuleValidator } from '../types';

export const stringRules: Record<string, RuleValidator> = {
    required: (value: any): boolean => {
        if (value === undefined || value === null) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
    },

    minLength: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return String(value).length >= params[0];
    },

    maxLength: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return String(value).length <= params[0];
    },

    length: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return String(value).length === params[0];
    },

    email: (value: any): boolean => {
        if (value === undefined || value === null || value === '') return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(String(value));
    },

    url: (value: any): boolean => {
        if (value === undefined || value === null || value === '') return true;
        try {
            new URL(String(value));
            return true;
        } catch {
            return false;
        }
    },

    regex: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        const regex = typeof params[0] === 'string' ? new RegExp(params[0]) : params[0];
        return regex.test(String(value));
    },

    alpha: (value: any): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return /^[A-Za-z]+$/.test(String(value));
    },

    alphaNumeric: (value: any): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return /^[A-Za-z0-9]+$/.test(String(value));
    },

    alphaDash: (value: any): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return /^[A-Za-z0-9_-]+$/.test(String(value));
    },

    startsWith: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return String(value).startsWith(params[0]);
    },

    endsWith: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return String(value).endsWith(params[0]);
    },

    contains: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return String(value).includes(params[0]);
    },

    notContains: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return !String(value).includes(params[0]);
    },

    in: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return params.includes(String(value));
    },

    notIn: (value: any, params: any[]): boolean => {
        if (value === undefined || value === null || value === '') return true;
        return !params.includes(String(value));
    },

    uuid: (value: any): boolean => {
        if (value === undefined || value === null || value === '') return true;
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(String(value));
    },

    ip: (value: any): boolean => {
        if (value === undefined || value === null || value === '') return true;
        const str = String(value);

        // IPv4 check
        const ipv4 = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (ipv4.test(str)) return true;

        // IPv6 (compressed/uncompressed, optional IPv4-mapped at the end)
        const isValidH16 = (seg: string) => /^[0-9a-fA-F]{1,4}$/.test(seg);
        const isValidIPv4 = (s: string) => ipv4.test(s);

        // Quick reject: triple colon implies overlapping '::' sequences (invalid)
        if (str.includes(':::')) return false;

        // Only one '::' allowed
        if ((str.match(/::/g) || []).length > 1) return false;

        const hasCompressed = str.includes('::');
        let head = '';
        let tail = '';

        if (hasCompressed) {
            const parts = str.split('::');
            head = parts[0];
            tail = parts[1] || '';
        } else {
            head = str;
            tail = '';
        }

        const headSegs = head ? head.split(':').filter(Boolean) : [];
        const tailSegs = tail ? tail.split(':').filter(Boolean) : [];

        // Determine if last segment is embedded IPv4 (only allowed at the very end)
        const lastHead = headSegs[headSegs.length - 1] || '';
        const lastTail = tailSegs[tailSegs.length - 1] || '';
        const hasEmbeddedIPv4 = lastTail.includes('.') || (!hasCompressed && lastHead.includes('.'));

        // Validate segments
        const validateSegs = (segs: string[], allowLastIPv4: boolean): boolean => {
            if (segs.length === 0) return true;
            const lastIndex = segs.length - 1;
            for (let i = 0; i < segs.length; i++) {
                const seg = segs[i];
                if (allowLastIPv4 && i === lastIndex && seg.includes('.')) {
                    if (!isValidIPv4(seg)) return false;
                } else {
                    if (!isValidH16(seg)) return false;
                }
            }
            return true;
        };

        if (!validateSegs(headSegs, !hasCompressed && hasEmbeddedIPv4)) return false;
        if (!validateSegs(tailSegs, hasCompressed && hasEmbeddedIPv4)) return false;

        // Count total segments; '::' can represent at least one segment
        const segCount = headSegs.length + tailSegs.length + (hasEmbeddedIPv4 ? 1 : 0);

        if (hasCompressed) {
            // With compression, total segments must be less than 8
            if (segCount >= 8) return false;
        } else {
            // Without compression: 8 segments, or 7 + embedded IPv4
            if (!(segCount === 8 || (hasEmbeddedIPv4 && segCount === 7))) return false;
        }

        return true;
    }
};